/**
 * Tag injection for Google Analytics + Google Ads.
 *
 * Tags only load when BOTH conditions hold:
 *   1. The Settings page toggled `ga_enabled` (or `gads_enabled`) on.
 *   2. The visitor granted `consent.analytics` (or `consent.ads`).
 *
 * Idempotent: a second call with the same id and the same consent is a
 * no-op. A consent change to `denied` removes nothing — Google Consent
 * Mode v2 handles runtime gating via the `consent.update` push that
 * `writeConsent` already emits. We just stop loading new tags.
 */

import { readConsent, subscribe, type ConsentState } from "@/lib/consent";

declare global {
  interface Window {
    dataLayer?: unknown[];
  }
}

let gaLoaded = false;
let adsLoaded = false;

function isBrowser(): boolean {
  return typeof window !== "undefined" && typeof document !== "undefined";
}

function injectScript(src: string, id: string): void {
  if (document.getElementById(id)) return;
  const tag = document.createElement("script");
  tag.async = true;
  tag.src = src;
  tag.id = id;
  document.head.appendChild(tag);
}

function configureGtag(measurementId: string): void {
  window.dataLayer = window.dataLayer ?? [];
  // Anonymous-equivalent helper for non-module callers.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  function gtag(...args: any[]) {
    window.dataLayer?.push(args);
  }
  gtag("js", new Date());
  gtag("config", measurementId, {
    anonymize_ip: true,
    transport_type: "beacon",
  });
}

/** Load GA when (ga_enabled && consent.analytics). */
function maybeLoadGa(state: ConsentState): void {
  if (!isBrowser() || gaLoaded) return;
  const enabled = window.localStorage.getItem("ga_enabled") === "true";
  const id = window.localStorage.getItem("ga_measurement_id") ?? "";
  if (!enabled || !state.analytics || !id) return;
  injectScript(`https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(id)}`, "ga-script");
  configureGtag(id);
  gaLoaded = true;
}

/** Load Google Ads when (gads_enabled && consent.ads). */
function maybeLoadAds(state: ConsentState): void {
  if (!isBrowser() || adsLoaded) return;
  const enabled = window.localStorage.getItem("gads_enabled") === "true";
  const id = window.localStorage.getItem("gads_conversion_id") ?? "";
  if (!enabled || !state.ads || !id) return;
  injectScript(`https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(id)}`, "gads-script");
  window.dataLayer = window.dataLayer ?? [];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  function gtag(...args: any[]) {
    window.dataLayer?.push(args);
  }
  gtag("config", id);
  adsLoaded = true;
}

/**
 * Bootstrap the analytics layer. Call once near the app root (the
 * ConsentProvider already does this). Subsequent consent updates flow in
 * via the `subscribe` channel.
 */
export function bootAnalytics(): () => void {
  if (!isBrowser()) return () => {};
  const initial = readConsent();
  maybeLoadGa(initial);
  maybeLoadAds(initial);
  const unsubscribe = subscribe((state) => {
    maybeLoadGa(state);
    maybeLoadAds(state);
  });
  return unsubscribe;
}
